import { ArrowIcon } from "assets/icons";
import cn from "classnames";
import { ChatComposer } from "modules/aiConversations";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon } from "ui";

import {
  AITicket,
  ESupportTicketKind,
  ESupportTicketPriority,
  ESupportTicketSource,
  TicketStatus,
} from "../../aiTickets.types";
import {
  useAddTicketNote,
  useTicketConversation,
  useTicketConversationActions,
  useTicketNotes,
  useTicketReturnContext,
  useTicketTranscript,
} from "../../apis";
import { useTicketChatRealTime } from "../../hooks";
import { ChannelIcon } from "../../components/ChannelIcon";
import { PriorityPill } from "../../components/PriorityPill";
import {
  AssignedAgentCell,
  StatusCell,
  TypeCell,
} from "../../components/TicketCells";
import { CreateReturnModal } from "../CreateReturnModal";

import styles from "./TicketModal.module.scss";

interface Props {
  ticket: AITicket;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange?: (next: TicketStatus) => void;
  onPriorityChange?: (next: ESupportTicketPriority) => void;
}

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M5 5l10 10M15 5L5 15"
      stroke="#090914"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 1l1.6 4.4L14 7l-4.4 1.6L8 13 6.4 8.6 2 7l4.4-1.6L8 1Z"
      fill="url(#grad-sparkle-modal)"
    />
    <defs>
      <linearGradient id="grad-sparkle-modal" x1="2" y1="1" x2="14" y2="13">
        <stop stopColor="#FB3973" />
        <stop offset="1" stopColor="#9933FF" />
      </linearGradient>
    </defs>
  </svg>
);

const PersonOutlineIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle
      cx="8"
      cy="6"
      r="3"
      stroke="#7E7E8B"
      strokeWidth="1.4"
      fill="none"
    />
    <path
      d="M2 14c0-2.5 2.5-4.5 6-4.5s6 2 6 4.5"
      stroke="#7E7E8B"
      strokeWidth="1.4"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M5.5 2.5 6.8 5 5.6 6.3a8 8 0 0 0 4.1 4.1L11 9.2l2.5 1.3v2.3c0 .6-.5 1-1 1A10.5 10.5 0 0 1 2.2 3.5c0-.6.4-1 1-1h2.3Z"
      stroke="#fb3973"
      strokeWidth="1.3"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const formatTime = (iso: string) => {
  const m = moment(iso);
  return m.isValid() ? m.format("LT") : "";
};

const TicketModal = ({
  ticket,
  isOpen,
  onClose,
  onStatusChange,
  onPriorityChange,
}: Props) => {
  const { t } = useTranslation();
  const ticketRaw = isOpen ? ticket.raw : null;
  const { messages, isLoading, hasConversation } =
    useTicketTranscript(ticketRaw);
  const { conversation } = useTicketConversation(ticketRaw);
  const { sendMessage, setMode } = useTicketConversationActions(ticketRaw);
  useTicketChatRealTime(ticketRaw);

  const { notes } = useTicketNotes(isOpen ? ticket.id : null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);

  // Returns are created FROM a return ticket and nowhere else: the ticket is
  // what carries the order, the complaint time the deadline is measured from,
  // and the media the assistant gathered.
  const isReturnRequest = ticket.raw.kind === ESupportTicketKind.ReturnRequest;
  // Also drives the button/badge: once every line of the order is in a return
  // there is nothing left to create, and offering the action anyway only leads
  // to a form the server would reject.
  const { data: returnContext } = useTicketReturnContext(
    isOpen && isReturnRequest ? ticket.id : null,
  );
  const addNote = useAddTicketNote(isOpen ? ticket.id : null);
  const [noteDraft, setNoteDraft] = useState("");

  const submitNote = () => {
    const text = noteDraft.trim();
    if (!text || addNote.isLoading) return;
    addNote.mutate(text, { onSuccess: () => setNoteDraft("") });
  };

  if (!isOpen) return null;

  const r = ticket.raw;
  const shortId = ticket.id.slice(0, 10).toUpperCase();
  const sourceLabel =
    r.source === ESupportTicketSource.WhatsApp
      ? t("WhatsApp")
      : t("In-App Support");

  const aiOn = conversation?.status === "ai";
  const isClosed = conversation?.status === "closed";
  const composerDisabled = !conversation || conversation.status !== "human";

  const composerHint = isClosed
    ? t("This conversation is closed.")
    : t(
        "AI is handling this conversation. Turn it off to send a message manually.",
      );

  return (
    // Close only on a real backdrop click. `onClick={onClose}` alone also fired
    // for clicks inside anything rendered through a React portal from in here —
    // the create-return modal — because React bubbles synthetic events along the
    // React tree even when the DOM node lives under `body`. Every click in that
    // form was closing the ticket, and the form with it.
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <div className={styles.header}>
          <div className={styles.ticketId}>{shortId}</div>
          <div className={styles.ticketTitle}>{ticket.title}</div>
          {ticket.body && (
            <div className={styles.ticketBody}>{ticket.body}</div>
          )}
        </div>

        <div className={styles.body}>
          <div className={styles.chatSide}>
            <div className={styles.chatHeader}>
              <div className={styles.contact}>
                <span className={styles.contactAvatar}>
                  {conversation?.contactAvatarUrl ? (
                    <img
                      src={conversation.contactAvatarUrl}
                      alt={conversation.contactName}
                      className={styles.contactAvatarImage}
                    />
                  ) : (
                    <ChannelIcon source={r.source} size={24} />
                  )}
                </span>
                <div className={styles.contactInfo}>
                  <div className={styles.contactName}>
                    {conversation?.contactName ?? sourceLabel}
                  </div>
                  <div className={styles.contactPhone}>
                    {conversation?.contactPhone ? (
                      <a
                        className={styles.contactPhoneLink}
                        href={`tel:${conversation.contactPhone}`}
                      >
                        {conversation.contactPhone}
                      </a>
                    ) : (
                      (conversation?.contactSub ?? sourceLabel)
                    )}
                  </div>
                </div>
              </div>

              {conversation && !isClosed && (
                <div className={styles.aiPill}>
                  <SparkleIcon />
                  <span>{t("AI for this conversation")}</span>
                  <button
                    type="button"
                    className={cn(styles.aiToggleBtn, {
                      [styles.aiToggleBtnOn]: aiOn,
                    })}
                    disabled={setMode.isLoading}
                    onClick={() => setMode.mutate(aiOn ? "human" : "ai")}
                    aria-label="Toggle AI"
                  >
                    <span
                      className={cn(styles.aiToggleKnob, {
                        [styles.aiToggleKnobOn]: aiOn,
                      })}
                    />
                  </button>
                  <span className={styles.switchState}>
                    {aiOn ? t("ON") : t("OFF")}
                  </span>
                </div>
              )}
            </div>

            {!hasConversation ? (
              <div className={styles.transcriptState}>
                {t("No conversation linked to this ticket.")}
              </div>
            ) : isLoading ? (
              <div className={styles.transcriptState}>{t("Loading...")}</div>
            ) : messages.length === 0 ? (
              <div className={styles.transcriptState}>
                {t("No messages yet.")}
              </div>
            ) : (
              <div className={styles.messages}>
                {messages.map((m) => {
                  const isCustomer = m.sender === "customer";
                  return (
                    <div
                      key={m.id}
                      className={
                        isCustomer
                          ? styles.clientMessageRow
                          : styles.aiMessageRow
                      }
                    >
                      <div
                        className={
                          isCustomer ? styles.clientBubble : styles.aiBubble
                        }
                      >
                        {m.mediaUrl && m.messageType === "image" && (
                          <img
                            className={styles.messageImage}
                            src={m.mediaUrl}
                            alt=""
                          />
                        )}
                        {m.mediaUrl && m.messageType === "voice" && (
                          <audio
                            controls
                            src={m.mediaUrl}
                            className={styles.messageAudio}
                          />
                        )}
                        {m.mediaUrl && m.messageType === "video" && (
                          <video
                            controls
                            preload="metadata"
                            src={m.mediaUrl}
                            className={styles.messageVideo}
                          />
                        )}
                        {m.text}
                        <span className={styles.messageBubbleTime}>
                          {formatTime(m.createdAt)}
                        </span>
                      </div>
                      {!isCustomer && (
                        <div className={styles.aiAvatar}>
                          {m.sender === "ai" ? "AI" : "A"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {hasConversation &&
              (composerDisabled ? (
                <div className={styles.composerDisabledHint}>
                  {composerHint}
                </div>
              ) : (
                <ChatComposer
                  onSend={(payload) => sendMessage.mutate(payload)}
                />
              ))}
          </div>

          <div className={styles.metaSide}>
            {isReturnRequest && (
              <div className={styles.metaField}>
                <div className={styles.metaLabel}>{t("Return")}</div>
                <div className={styles.metaValue}>{ticket.raw.orderId}</div>
                {returnContext?.canCreateReturn ? (
                  <button
                    type="button"
                    className={styles.notesSave}
                    onClick={() => setIsReturnModalOpen(true)}
                  >
                    {t("Create return")}
                  </button>
                ) : returnContext?.hasReturn ? (
                  <>
                    <div className={styles.returnBadge}>
                      {returnContext.allItemsReturned
                        ? t("Return created")
                        : t("Return in progress")}
                    </div>
                    {/* Straight to the return rather than back out to the
                        Returns list and a search for the order. */}
                    <Link
                      className={styles.returnLink}
                      to={{
                        pathname: "/returns",
                        search: `returnId=${ticket.raw.orderId}`,
                      }}
                      target="_blank"
                    >
                      <span>{t("Open return")}</span>
                      <Icon
                        as={ArrowIcon}
                        boxSize={16}
                        className={styles.returnLinkArrow}
                        color="#f4318f"
                      />
                    </Link>
                    {/* The case that produced a raw server error before: half
                        the order returned, an agent coming back for the rest.
                        A return is one status with one reason and one set of
                        evidence, so the remainder needs its own — after this
                        one closes. */}
                    {!returnContext.allItemsReturned && (
                      <div className={styles.returnHint}>
                        {t(
                          "Covers part of the order. The remaining items cannot be added while this return is running.",
                        )}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}

            <div className={styles.metaField}>
              <div className={styles.metaLabel}>{t("Status")}</div>
              <StatusCell
                status={ticket.status}
                onChange={(next) => onStatusChange?.(next)}
              />
            </div>

            <div className={styles.metaField}>
              <div className={styles.metaLabel}>{t("Type")}</div>
              {onPriorityChange ? (
                <TypeCell
                  priority={r.priority}
                  onChange={(next) => onPriorityChange(next)}
                />
              ) : (
                <PriorityPill priority={r.priority} withChevron={false} />
              )}
            </div>

            <div className={styles.metaField}>
              <div className={styles.metaLabel}>{t("Source")}</div>
              <div className={styles.metaValue}>{sourceLabel}</div>
            </div>

            <div className={styles.metaField}>
              <div className={styles.metaLabel}>{t("Date created")}</div>
              <div className={styles.metaValue}>{ticket.dateCreated}</div>
            </div>

            <div className={styles.metaField}>
              <div className={styles.metaLabel}>{t("Assigned agent")}</div>
              <AssignedAgentCell source={r.source} />
            </div>

            {conversation && (
              <div className={styles.metaField}>
                <div className={styles.metaLabel}>
                  {t("Customer information")}
                </div>
                {conversation.contactPhone && (
                  <a
                    className={styles.metaPhone}
                    href={`tel:${conversation.contactPhone}`}
                  >
                    <PhoneIcon />
                    <span>{conversation.contactPhone}</span>
                  </a>
                )}
                <div className={styles.metaCustomer}>
                  <PersonOutlineIcon />
                  <span>{conversation.contactName}</span>
                </div>
              </div>
            )}

            <div className={styles.metaField}>
              <div className={styles.metaLabel}>{t("Notes")}</div>

              <div className={styles.notesThread}>
                {notes.length === 0 ? (
                  <div className={styles.notesEmpty}>{t("No notes yet.")}</div>
                ) : (
                  notes.map((note) => {
                    const authorName = note.author
                      ? `${note.author.firstName} ${note.author.lastName}`.trim()
                      : t("Unknown agent");
                    return (
                      <div key={note.id} className={styles.noteItem}>
                        <div className={styles.noteHead}>
                          <span className={styles.noteAuthor}>
                            {authorName || t("Unknown agent")}
                          </span>
                          <span className={styles.noteTime}>
                            {moment(note.createdAt).format("DD MMM, HH:mm")}
                          </span>
                        </div>
                        <div className={styles.noteText}>{note.text}</div>
                      </div>
                    );
                  })
                )}
              </div>

              <textarea
                className={styles.notesInput}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder={t("Add notes for other agents…")}
                rows={3}
                maxLength={5000}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    submitNote();
                  }
                }}
              />
              <button
                type="button"
                className={styles.notesSave}
                disabled={!noteDraft.trim() || addNote.isLoading}
                onClick={submitNote}
              >
                {t("Add note")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isReturnRequest && (
        <CreateReturnModal
          source={{ kind: "ticket", ticketId: ticket.id }}
          defaultReason={ticket.title}
          defaultDescription={ticket.raw.aiSummary}
          isOpen={isReturnModalOpen}
          onClose={() => setIsReturnModalOpen(false)}
        />
      )}
    </div>
  );
};

export default TicketModal;
